/*
  Zorali: storefront tweaks for the MagicX "AI Autocomplete" app.
  Loaded from layout/theme.liquid (kept under the original asset name so the include never changes).

  1. Option and chip icons off: embed config, SDK options and a CSS fallback inside the widget's shadow root.
  2. "AI Autocomplete" badge removed from the dropdown footer (the "tab to select" hint stays).
  3. Dropdown no longer opens on page load: the SDK's autoFocus is switched off, so it opens on focus/typing.
  4. Spare-part products (Replacement ..., $1 parts, GWPs) hidden from the Products row, and the
     "Replacements" product-type option hidden from the options grid. The app's catalog sync includes
     products that are not published to the Online Store, and their cards 404 when clicked.
  5. The theme's rotating "Search for ..." label (header.liquid, setting collection_names) shows over the
     widget while it is empty and unfocused. Companion CSS lives in assets/custom.css (.zorali-ai-idle).
  6. Safari / iOS fix for text typing in reverse. See installShadowSelection below.
  7. Theme layout metafields (st_*) kept out of the refinement steps. See JUNK_PARAM below.
*/
(() => {
  'use strict';

  const HOST_SELECTOR = '.ai-search-bar__sdk';
  const FRAME_SELECTOR = '.ai-search-embed-frame';
  const SDK_GLOBAL = 'MagicxAIAutocomplete';
  const STYLE_ID = 'zorali-ai-search-tweaks';
  const IDLE_CLASS = 'zorali-ai-idle';
  const HIDDEN_ATTR = 'data-zorali-hidden';

  // Products whose handle matches, or whose price is below this, are spare parts or giveaways.
  const EXCLUDE_HANDLE = /(replacement|spare|repair|gwp|(^|-)parts?(-|$))/i;
  const MIN_PRICE = 5;
  const EXCLUDE_OPTION = /^replacements?$/i;

  const SDK_OVERRIDES = { autoFocus: false, showOptionIcons: false, showChipIcons: false };

  // ---- 7: keep theme-layout metafields out of the refinement steps ----------------------
  // The app syncs every Shopify product metafield and can offer any of them as a refinement step, labelled
  // with the raw metafield key. That turned the theme's PDP layout fields into questions: "neck gaiter"
  // asked for "st features section main title" (values like "A fleece that lives up to its name") before
  // showing any products. Measured 2026-09-18: an st_* step on 5 of 12 everyday searches.
  //
  // It cannot be fixed at the source. The app's Custom fields tab is locked ("update your Shopify catalog
  // and resync"); Shopify refuses to restrict app access on merchant-owned metafield definitions
  // ("must be one of [public_read_write]"); deleting the definitions would remove the fields from the
  // Shopify admin where the team edits them; and the app labels steps from the metafield KEY, so renaming
  // the definition changes nothing. Guidance was added on ai-autocomplete.com (the REFINEMENT FIELDS
  // block), which helps but steers a model rather than removing the field. This is the deterministic half.
  //
  // Two shapes, two answers:
  //   a. The junk step arrives alongside real ones -> drop it from the response. We run before the SDK, so
  //      the fetch reference it captures is this wrapper.
  //   b. The junk step is the ONLY thing the server returned -> dropping it would leave an empty dropdown
  //      and no products, so instead let it through and skip it the moment it becomes the active step,
  //      which asks the server for the next answer. That is the SDK's own skip, as if the shopper clicked.
  // Fail open throughout: a search that works is worth more than a filter that fires.
  // Remove once MagicX ship a fix.
  const SUGGEST_URL = /(^|\.)ai-autocomplete\.com\/api\/suggest/;
  const JUNK_PARAM = /^st_/;
  // The server can chain several of these in a row: "rain jacket" offered three ST fields in turn on
  // 2026-09-18 before moving on to a real price step. The cap is per query, not per session, so a long
  // session cannot exhaust it, and it still stops a runaway loop if the server only ever returns junk.
  const MAX_AUTO_SKIPS = 6;

  const isReal = (s) => s && typeof s.type === 'string' && s.type !== 'placeholder';
  const isJunk = (s) => s && typeof s.type === 'string' && JUNK_PARAM.test(s.type);

  function filterSuggestPayload(payload) {
    if (!payload || !payload.data || !Array.isArray(payload.data.suggestions)) return null;
    const all = payload.data.suggestions;
    const kept = all.filter((s) => !isJunk(s));
    if (kept.length === all.length) return null;
    // Case b: nothing real would survive. Leave the payload alone and let the skip below handle it.
    if (!kept.some(isReal)) return null;
    return Object.assign({}, payload, {
      data: Object.assign({}, payload.data, { suggestions: kept })
    });
  }

  const nativeFetch = window.fetch;
  if (typeof nativeFetch === 'function') {
    window.fetch = function (...args) {
      const request = args[0];
      const url = typeof request === 'string' ? request : (request && request.url) || '';
      const response = nativeFetch.apply(this, args);
      if (!SUGGEST_URL.test(url)) return response;
      return response.then((res) => {
        if (!res || !res.ok) return res;
        return res
          .clone()
          .json()
          .then((payload) => {
            const filtered = filterSuggestPayload(payload);
            if (!filtered) return res;
            // Fresh headers: the original Content-Length and Content-Encoding no longer describe this body.
            return new Response(JSON.stringify(filtered), {
              status: res.status,
              statusText: res.statusText,
              headers: { 'Content-Type': 'application/json' }
            });
          })
          .catch(() => res);
      });
    };
  }

  // Case b: skip a junk step that arrived on its own.
  function wireJunkSkip(ac) {
    if (!ac || ac.__zoraliJunkSkip) return;
    if (!ac.store || typeof ac.store.subscribe !== 'function' || typeof ac.skipActivePill !== 'function') return;
    ac.__zoraliJunkSkip = true;
    let skips = 0;
    let lastText = null;
    ac.store.subscribe((state) => {
      if (!state) return;
      if (state.text !== lastText) { lastText = state.text; skips = 0; }
      if (skips >= MAX_AUTO_SKIPS) return;
      if (state.editingParam || state.inSelectionAnimation) return;
      const active = (state.suggestions || []).filter(isReal)[0];
      if (!isJunk(active)) return;
      skips += 1;
      // Out of the subscriber, so the SDK finishes this state transition first.
      setTimeout(() => {
        try { ac.skipActivePill(); } catch (e) { /* ignore */ }
      }, 0);
    });
  }

  // ---- 1 + 3: embed config and SDK constructor overrides ---------------------------------
  const embedConfig = window.__AI_SEARCH_EMBED_CONFIG__;
  if (embedConfig) {
    embedConfig.showOptionIcons = false;
    embedConfig.showChipIcons = false;
  }

  function patchSdk(sdk) {
    if (!sdk || typeof sdk !== 'object' || typeof sdk.AIAutocomplete !== 'function') return sdk;
    if (sdk.__zoraliPatched) return sdk;
    const Original = sdk.AIAutocomplete;
    class ZoraliAIAutocomplete extends Original {
      constructor(container, options) {
        super(container, Object.assign({}, options || {}, SDK_OVERRIDES));
      }
    }
    // The SDK's export object is getter-only, so hand back a proxy instead of reassigning.
    return new Proxy(sdk, {
      get(target, key) {
        if (key === 'AIAutocomplete') return ZoraliAIAutocomplete;
        if (key === '__zoraliPatched') return true;
        return target[key];
      }
    });
  }

  const descriptor = Object.getOwnPropertyDescriptor(window, SDK_GLOBAL);
  if (descriptor && 'value' in descriptor) {
    // SDK already loaded (script order changed). Patch in place; the embed reads the global when it runs.
    if (descriptor.writable) window[SDK_GLOBAL] = patchSdk(descriptor.value);
  } else if (!descriptor) {
    // Normal case: this script runs before the SDK. Its `var MagicxAIAutocomplete = ...` lands on this setter.
    let stored;
    Object.defineProperty(window, SDK_GLOBAL, {
      configurable: true,
      enumerable: true,
      get() { return stored; },
      set(value) { stored = patchSdk(value); }
    });
  }

  // Fallback for instances that were created before this script ran.
  function fixExistingInstances() {
    const handles = window.__aiSearchEmbed;
    if (!Array.isArray(handles)) return;
    for (const handle of handles) {
      const ac = handle && handle.ac;
      if (!ac) continue;
      wireJunkSkip(ac);
      if (ac.__zoraliFixed) continue;
      ac.__zoraliFixed = true;
      try { ac.update({ showOptionIcons: false, showChipIcons: false }); } catch (e) { /* older SDK */ }
      if (!userHasInteracted && typeof ac.blur === 'function') {
        try { ac.blur(); } catch (e) { /* ignore */ }
      }
    }
  }

  let userHasInteracted = false;
  const markInteraction = () => { userHasInteracted = true; };
  document.addEventListener('pointerdown', markInteraction, { capture: true, once: true });
  document.addEventListener('keydown', markInteraction, { capture: true, once: true });

  // ---- 6: Safari / iOS "types backwards" fix -------------------------------------------
  // The widget's field is a contenteditable inside an open shadow root. The SDK reads the caret with
  // `shadowRoot.getSelection?.() || document.getSelection()`. WebKit has no ShadowRoot.getSelection and its
  // document selection does not resolve into a shadow tree, so the SDK reads a null caret offset. It then
  // re-renders the field with replaceChildren() and only restores the caret when that offset is non-null,
  // so the caret lands back at position 0 and every new character is prepended: "overland" typed as
  // "DNALREVO". (The SDK also upper-cases the first character on every keystroke, which is why the reversed
  // text comes out in caps.) Fix: give the widget's shadow root a getSelection() built on the standard
  // Selection.getComposedRanges(), which WebKit does support, and write the caret back with
  // setBaseAndExtent, which WebKit honours for shadow nodes where addRange does not.
  // Vendor bug, reproduced on the live site with this file disabled. Remove once MagicX ship a fix.
  function composedRanges(selection, root) {
    try {
      const ranges = selection.getComposedRanges({ shadowRoots: [root] });
      if (ranges && ranges.length) return ranges;
    } catch (e) { /* older signature */ }
    try {
      const ranges = selection.getComposedRanges(root);
      if (ranges && ranges.length) return ranges;
    } catch (e) { /* unsupported */ }
    return null;
  }

  function selectionShim(selection, range) {
    return {
      get rangeCount() { return 1; },
      get anchorNode() { return range.startContainer; },
      get anchorOffset() { return range.startOffset; },
      get focusNode() { return range.endContainer; },
      get focusOffset() { return range.endOffset; },
      get isCollapsed() { return range.collapsed; },
      getRangeAt() { return range; },
      removeAllRanges() { try { selection.removeAllRanges(); } catch (e) { /* ignore */ } },
      addRange(r) {
        try {
          selection.setBaseAndExtent(r.startContainer, r.startOffset, r.endContainer, r.endOffset);
          return;
        } catch (e) { /* fall through */ }
        try { selection.addRange(r); } catch (e) { /* ignore */ }
      },
      toString() { return range.toString(); }
    };
  }

  function installShadowSelection(root) {
    if (!root || typeof root.getSelection === 'function') return; // Chrome already has it
    const doc = root.ownerDocument || document;
    Object.defineProperty(root, 'getSelection', {
      configurable: true,
      value() {
        const selection = doc.getSelection();
        if (!selection || typeof selection.getComposedRanges !== 'function') return selection;
        const ranges = composedRanges(selection, root);
        const first = ranges && ranges[0];
        if (!first || !first.startContainer || !root.contains(first.startContainer)) return selection;
        const live = doc.createRange();
        try {
          live.setStart(first.startContainer, first.startOffset);
          live.setEnd(first.endContainer, first.endOffset);
        } catch (e) {
          return selection;
        }
        return selectionShim(selection, live);
      }
    });
  }

  // ---- 2 + 4 + 5: styles injected into the widget's (open) shadow root --------------------
  const SHADOW_CSS = `
    .magicx-aia-option-icon,
    .magicx-aia-segment-icon { display: none !important; }
    .magicx-aia-option-content:has(.magicx-aia-option-icon) {
      padding-left: 0 !important;
      text-indent: 0 !important;
    }
    .magicx-aia-footer-brand-link { display: none !important; }
    .magicx-aia-product[${HIDDEN_ATTR}],
    .magicx-aia-option[${HIDDEN_ATTR}] { display: none !important; }
    .magicx-aia-products:not(:has(.magicx-aia-product:not([${HIDDEN_ATTR}]))) { display: none !important; }
    /* The theme's rotating label owns the empty, unfocused state; the SDK's own placeholder shows on focus. */
    :host(:not(:focus-within)) .magicx-aia-input[data-aia-empty="true"]::before { content: none !important; }
  `;

  function handleFromUrl(href) {
    if (!href) return '';
    const match = /\/products\/([^/?#]+)/.exec(href);
    return match ? decodeURIComponent(match[1]) : '';
  }

  function priceFromText(text) {
    if (!text) return NaN;
    const match = /\d[\d,]*(?:\.\d+)?/.exec(text);
    return match ? parseFloat(match[0].replace(/,/g, '')) : NaN;
  }

  function isSparePart(card) {
    const handle = handleFromUrl(card.getAttribute('href'));
    const title = (card.querySelector('.magicx-aia-product-title') || {}).textContent || '';
    if (EXCLUDE_HANDLE.test(handle) || EXCLUDE_HANDLE.test(title.replace(/\s+/g, '-'))) return true;
    const price = priceFromText((card.querySelector('.magicx-aia-product-price') || {}).textContent);
    return Number.isFinite(price) && price < MIN_PRICE;
  }

  function hide(el) {
    el.setAttribute(HIDDEN_ATTR, '');
    el.setAttribute('aria-hidden', 'true');
    el.tabIndex = -1;
  }

  function filterProducts(root) {
    root.querySelectorAll('.magicx-aia-product').forEach((card) => {
      if (!card.hasAttribute(HIDDEN_ATTR) && isSparePart(card)) hide(card);
    });
    root.querySelectorAll('.magicx-aia-option').forEach((option) => {
      if (option.hasAttribute(HIDDEN_ATTR)) return;
      const text = ((option.querySelector('.magicx-aia-option-text') || option).textContent || '').trim();
      if (EXCLUDE_OPTION.test(text)) hide(option);
    });
  }

  // ---- 5: rotating label state -------------------------------------------------------------
  function updateIdle(host) {
    const frame = host.closest(FRAME_SELECTOR);
    if (!frame || !frame.querySelector('#label_tochange')) return;
    const root = host.shadowRoot;
    const input = root && root.querySelector('.magicx-aia-input');
    const empty = !input || input.getAttribute('data-aia-empty') !== 'false';
    const focused = frame.matches(':focus-within');
    frame.classList.toggle(IDLE_CLASS, empty && !focused);
  }

  const wiredHosts = new WeakSet();

  function wireHost(host) {
    const root = host.shadowRoot;
    if (!root) return;
    installShadowSelection(root);
    if (!root.getElementById(STYLE_ID)) {
      const style = document.createElement('style');
      style.id = STYLE_ID;
      style.textContent = SHADOW_CSS;
      root.appendChild(style);
    }
    filterProducts(root);
    updateIdle(host);
    if (wiredHosts.has(host)) return;
    wiredHosts.add(host);

    let queued = false;
    const schedule = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        filterProducts(root);
        updateIdle(host);
      });
    };
    new MutationObserver(schedule).observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-aia-empty', 'href']
    });
    // focusin/focusout are composed, so they cross the shadow boundary. Defer so :focus-within has settled.
    const frame = host.closest(FRAME_SELECTOR) || host;
    frame.addEventListener('focusin', () => setTimeout(() => updateIdle(host), 0));
    frame.addEventListener('focusout', () => setTimeout(() => updateIdle(host), 0));
  }

  let scanQueued = false;
  function scan() {
    scanQueued = false;
    fixExistingInstances();
    document.querySelectorAll(HOST_SELECTOR).forEach(wireHost);
  }
  function scheduleScan() {
    if (scanQueued) return;
    scanQueued = true;
    requestAnimationFrame(scan);
  }

  new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node.nodeType === 1 && (node.matches(HOST_SELECTOR) || node.querySelector(HOST_SELECTOR))) {
          scheduleScan();
          return;
        }
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener('focusin', scheduleScan, true);
  document.addEventListener('shopify:section:load', scheduleScan);
  window.addEventListener('load', scheduleScan, { once: true });
  if (document.readyState !== 'loading') scheduleScan();
  else document.addEventListener('DOMContentLoaded', scheduleScan, { once: true });
})();
